import React, { useMemo } from 'react';
import { Flex, TextV2 } from '@withjoy/joykit';
import type { StandardHotelPricingValues, AccommodationsConsoleState } from '@shared/types/Accommodations.types';
import { useTranslation } from '@shared/core';
import { translatedPrice } from '@shared/utils/accommodationPrice';
import { readNavigatorLanguage } from '@shared/utils/getNavigatorLanguage';
import { useTravelWhiteLabelCurrency } from '@shared/hooks/useTravelWhiteLabelCurrency';
import { useAccommodationsListingMobileLayout } from '@apps/stays/AccommodationsConsole/hooks/useAccommodationsListingMobileLayout';
import { CASH_FUND_GOLD, CASH_FUND_STATIC_AVAILABLE_BALANCE } from '../../cashFund/cashFund.constants';

// Standard hotels only: room blocks/custom links can't use the fund (the caller gates on
// listing.type === 'standardHotel'), so the props are narrowed — never a room block.
type CashFundCostBreakdownProps = Readonly<{
  pricingInfo: StandardHotelPricingValues;
  occupancies: AccommodationsConsoleState['occupancies'];
  noOfNights: number;
}>;

// Cash-fund cost area: reframes pricing around the couple's fund balance.
// The fund (a total balance) is applied to the WHOLE stay total:
//   fund covers (total)   = min(balance, stayTotal)             → "Fund covers $X /night" = / nights
//   out-of-pocket (total) = max(0, stayTotal − balance)         → "+$Y /night" = / nights, "$Z total"
// The original stay total is shown struck-through. All static until BE returns the real balance.
export const CashFundCostBreakdown = ({ pricingInfo, occupancies, noOfNights }: CashFundCostBreakdownProps) => {
  const { isMobileOrTablet } = useAccommodationsListingMobileLayout();
  const { tq } = useTranslation('accommodationsConsole');
  const { currency: fallbackCurrency } = useTravelWhiteLabelCurrency();
  const navigatorLanguage = readNavigatorLanguage();

  const { nightlyBaseRate, totalRate, totalRateString, totalRateCurrency, totalRateDigits } = pricingInfo;

  // Fall back to nightlyBaseRate × nights if a numeric stay total isn't available.
  const stayTotal = totalRate ?? nightlyBaseRate * noOfNights;
  const nights = Math.max(1, noOfNights);

  const fundCoversTotal = Math.min(CASH_FUND_STATIC_AVAILABLE_BALANCE, stayTotal);
  const outOfPocketTotal = Math.max(0, stayTotal - CASH_FUND_STATIC_AVAILABLE_BALANCE);

  // Format in the SAME currency/digits that produced `totalRateString`, not the white-label currency —
  // otherwise a non-USD visitor could see e.g. a "$500" total but "Fund covers €400" (mixed/overstated).
  const currency = totalRateCurrency ?? fallbackCurrency;
  const digits = totalRateDigits ?? 2;
  const minorDivisor = Math.pow(10, digits);
  const toPrice = (majorUnits: number) => translatedPrice(Math.round(majorUnits * minorDivisor), digits, currency, navigatorLanguage);
  const fundCoversString = toPrice(fundCoversTotal / nights);
  const outOfPocketString = toPrice(outOfPocketTotal / nights);
  const afterFundTotalString = toPrice(outOfPocketTotal);

  const occupancyText = useMemo(() => {
    // tq.plural delegates singular/plural to the i18n runtime's locale plural rules (one/other/few/
    // many/...), so each count is correct in every language — unlike a hardcoded 1-vs-many branch.
    const roomCount = occupancies.unitCount ?? 0;
    const nights = tq.plural(noOfNights, { one: tq`1 night`, other: tq`${noOfNights} nights` });
    const rooms = tq.plural(roomCount, { one: tq`1 room`, other: tq`${roomCount} rooms` });
    return tq`${nights}, ${rooms}, incl. taxes and fees`;
  }, [tq, noOfNights, occupancies.unitCount]);

  return (
    <Flex
      flexDirection="column"
      alignItems="flex-end"
    >
      <TextV2
        typographyVariant="label1"
        color="mono14"
      >
        {tq`Fund covers ${fundCoversString} /night`}
      </TextV2>
      <Flex
        columnGap={1}
        alignItems="center"
        marginTop={1}
      >
        <TextV2
          typographyVariant={isMobileOrTablet ? 'hed1' : 'hed6'}
          fontWeight={600}
          color={CASH_FUND_GOLD}
        >
          {tq`+${outOfPocketString}`}
        </TextV2>
        <TextV2
          typographyVariant={isMobileOrTablet ? 'label1' : 'label2'}
          color="mono10"
          fontWeight={400}
        >
          {tq`/ night`}
        </TextV2>
      </Flex>
      {totalRateString && (
        <>
          <Flex
            columnGap={1}
            alignItems="center"
            marginTop={1}
          >
            <TextV2
              textDecoration="line-through"
              color="mono10"
              typographyVariant={isMobileOrTablet ? 'label1' : 'label2'}
            >
              {totalRateString}
            </TextV2>
            <TextV2
              textAlign="end"
              typographyVariant={isMobileOrTablet ? 'label1' : 'label2'}
              color="mono10"
            >
              {tq`${afterFundTotalString} total`}
            </TextV2>
          </Flex>
          <TextV2
            marginTop={1}
            textAlign="end"
            typographyVariant="label1"
            color="mono10"
          >
            {occupancyText}
          </TextV2>
        </>
      )}
    </Flex>
  );
};
